import { gql } from '@apollo/client';

export const RESTOCK_SHIPMENT_FRAGMENT = gql`
  fragment RestockShipmentFragment on RestockShipment {
    id
    createdAt
    freightCost
    freightCostCurrency
    deliveryAddress
    shipName
    title
    totalWeight
    inventory {
      total {
        quantity
        amount
        amountCurrency
      }
      sold {
        quantity
        amount
        amountCurrency
      }
    }
  }
`;

export const RESTOCK_SHIPMENT_PRODUCT_FRAGMENT = gql`
  fragment RestockShipmentProductFragment on RestockShipmentProduct {
    id
    restockShipmentId
    supplierProductId
    externalProductPageLink
    externalOrderPageLink
    externalOrderId
    sellerCompany
    sellerPhone
    shippingCompany
    shippingTrackingNumber
    shippingLogisticsNumber
  }
`;

export const RESTOCK_SHIPMENT_PRODUCT_VARIATION_FRAGMENT = gql`
  fragment RestockShipmentProductVariationFragment on RestockShipmentProductVariation {
    id
    restockShipmentProductId
    supplierProductVariationSku
    supplierProductVariationSupplierProductId
    quantity
    estimatedTotalCartonsBoxes
    estimatedTotalWeight
    localTotalCostOfGoods
    localTotalCostOfGoodsCurrency
    totalCost
    totalCostCurrency
  }
`;

export const RESTOCK_SHIPMENT_CARTON_BOX_FRAGMENT = gql`
  fragment RestockShipmentCartonBoxFragment on RestockShipmentCartonBox {
    id
    restockShipmentId
    size1
    size2
    size3
    weight
    volumeWeight
    finalWeight {
      weight
      type
    }
    freightCost
    deliveredAt
    trackingNumber
  }
`;

// Queries
export const GET_RESTOCK_SHIPMENTS = gql`
  ${RESTOCK_SHIPMENT_FRAGMENT}
  query GetRestockShipments {
    restockShipments {
      ...RestockShipmentFragment
    }
  }
`;

export const GET_RESTOCK_SHIPMENT = gql`
  ${RESTOCK_SHIPMENT_FRAGMENT}
  ${RESTOCK_SHIPMENT_PRODUCT_FRAGMENT}
  ${RESTOCK_SHIPMENT_PRODUCT_VARIATION_FRAGMENT}
  ${RESTOCK_SHIPMENT_CARTON_BOX_FRAGMENT}
  query GetRestockShipment($id: ID!) {
    restockShipment(id: $id) {
      ...RestockShipmentFragment
      products {
        ...RestockShipmentProductFragment
        supplierProduct {
          id
          name
          supplierName
        }
        variations {
          ...RestockShipmentProductVariationFragment

          cost {
            unitCost
            totalCost
            totalFreightCost
            totalProductCost
          }

          supplierProductVariation {
            sku
            supplierProductId
            typeValues {
              id
              value
              type {
                type
              }
            }
          }
        }
      }
      cartonBoxes {
        ...RestockShipmentCartonBoxFragment
        linkedProductVariations {
          restockShipmentCartonBoxId
          restockShipmentProductVariationId
          quantity
          boxFillCoeff
        }
      }
    }
  }
`;

// Mutations
export const CREATE_RESTOCK_SHIPMENT = gql`
  ${RESTOCK_SHIPMENT_FRAGMENT}
  mutation CreateRestockShipment($input: CreateRestockShipmentInput!) {
    createRestockShipment(input: $input) {
      ...RestockShipmentFragment
    }
  }
`;

export const UPDATE_RESTOCK_SHIPMENT = gql`
  ${RESTOCK_SHIPMENT_FRAGMENT}
  mutation UpdateRestockShipment($id: ID!, $input: UpdateRestockShipmentInput!) {
    updateRestockShipment(id: $id, input: $input) {
      ...RestockShipmentFragment
    }
  }
`;

export const DELETE_RESTOCK_SHIPMENT = gql`
  mutation DeleteRestockShipment($id: ID!) {
    deleteRestockShipment(id: $id)
  }
`;

export const CREATE_RESTOCK_SHIPMENT_PRODUCT = gql`
  ${RESTOCK_SHIPMENT_PRODUCT_FRAGMENT}
  mutation CreateRestockShipmentProduct($input: CreateRestockShipmentProductInput!) {
    createRestockShipmentProduct(input: $input) {
      ...RestockShipmentProductFragment
    }
  }
`;

export const UPDATE_RESTOCK_SHIPMENT_PRODUCT = gql`
  ${RESTOCK_SHIPMENT_PRODUCT_FRAGMENT}
  mutation UpdateRestockShipmentProduct($id: ID!, $input: UpdateRestockShipmentProductInput!) {
    updateRestockShipmentProduct(id: $id, input: $input) {
      ...RestockShipmentProductFragment
    }
  }
`;

export const DELETE_RESTOCK_SHIPMENT_PRODUCT = gql`
  mutation DeleteRestockShipmentProduct($id: ID!) {
    deleteRestockShipmentProduct(id: $id)
  }
`;

export const CREATE_RESTOCK_SHIPMENT_PRODUCT_VARIATION = gql`
  ${RESTOCK_SHIPMENT_PRODUCT_VARIATION_FRAGMENT}
  mutation CreateRestockShipmentProductVariation($input: CreateRestockShipmentProductVariationInput!) {
    createRestockShipmentProductVariation(input: $input) {
      ...RestockShipmentProductVariationFragment
    }
  }
`;

export const UPDATE_RESTOCK_SHIPMENT_PRODUCT_VARIATION = gql`
  ${RESTOCK_SHIPMENT_PRODUCT_VARIATION_FRAGMENT}
  mutation UpdateRestockShipmentProductVariation($id: ID!, $input: UpdateRestockShipmentProductVariationInput!) {
    updateRestockShipmentProductVariation(id: $id, input: $input) {
      ...RestockShipmentProductVariationFragment
    }
  }
`;

export const DELETE_RESTOCK_SHIPMENT_PRODUCT_VARIATION = gql`
  mutation DeleteRestockShipmentProductVariation($id: ID!) {
    deleteRestockShipmentProductVariation(id: $id)
  }
`;

export const CREATE_RESTOCK_SHIPMENT_CARTON_BOX = gql`
  ${RESTOCK_SHIPMENT_CARTON_BOX_FRAGMENT}
  mutation CreateRestockShipmentCartonBox($input: CreateRestockShipmentCartonBoxInput!) {
    createRestockShipmentCartonBox(input: $input) {
      ...RestockShipmentCartonBoxFragment
    }
  }
`;

export const UPDATE_RESTOCK_SHIPMENT_CARTON_BOX = gql`
  ${RESTOCK_SHIPMENT_CARTON_BOX_FRAGMENT}
  mutation UpdateRestockShipmentCartonBox($id: ID!, $input: UpdateRestockShipmentCartonBoxInput!) {
    updateRestockShipmentCartonBox(id: $id, input: $input) {
      ...RestockShipmentCartonBoxFragment
    }
  }
`;

export const DELETE_RESTOCK_SHIPMENT_CARTON_BOX = gql`
  mutation DeleteRestockShipmentCartonBox($id: ID!) {
    deleteRestockShipmentCartonBox(id: $id)
  }
`;

export const LINK_CARTON_BOX_TO_VARIATION = gql`
  ${RESTOCK_SHIPMENT_CARTON_BOX_FRAGMENT}
  mutation LinkCartonBoxToVariation($input: LinkCartonBoxToVariationInput!) {
    linkCartonBoxToVariation(input: $input) {
      ...RestockShipmentCartonBoxFragment
    }
  }
`;

export const UPDATE_CARTON_BOX_VARIATION_LINK = gql`
  ${RESTOCK_SHIPMENT_CARTON_BOX_FRAGMENT}
  mutation UpdateCartonBoxVariationLink($cartonBoxId: ID!, $variationId: ID!, $input: UpdateCartonBoxVariationLinkInput!) {
    updateCartonBoxVariationLink(cartonBoxId: $cartonBoxId, variationId: $variationId, input: $input) {
      ...RestockShipmentCartonBoxFragment
    }
  }
`;

export const UNLINK_CARTON_BOX_FROM_VARIATION = gql`
  ${RESTOCK_SHIPMENT_CARTON_BOX_FRAGMENT}
  mutation UnlinkCartonBoxFromVariation($cartonBoxId: ID!, $variationId: ID!) {
    unlinkCartonBoxFromVariation(cartonBoxId: $cartonBoxId, variationId: $variationId) {
      ...RestockShipmentCartonBoxFragment
    }
  }
`;

